import { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

export default function DatePicker({ name, defaultValue, required, className }) {
    const { i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(defaultValue ? new Date(defaultValue) : null);
    const [currentMonth, setCurrentMonth] = useState(defaultValue ? new Date(defaultValue) : new Date());
    const [inputValue, setInputValue] = useState(() => {
        if (!defaultValue) return '';
        const d = new Date(defaultValue);
        if (isNaN(d.getTime())) return defaultValue;
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
    });
    const containerRef = useRef(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();

    const lang = i18n.language === 'uz' ? 'uz' : 'ru';
    
    const monthNames = Array.from({ length: 12 }, (_, i) => {
        const str = new Intl.DateTimeFormat(lang, { month: 'long' }).format(new Date(2000, i, 1));
        return str.charAt(0).toUpperCase() + str.slice(1);
    });

    const dayNames = Array.from({ length: 7 }, (_, i) => {
        const str = new Intl.DateTimeFormat(lang, { weekday: 'short' }).format(new Date(2000, 0, i + 2));
        return str.charAt(0).toUpperCase() + str.slice(1);
    });

    const handlePrevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    const handleNextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));

    const handleSelectDate = (day) => {
        const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
        setSelectedDate(newDate);
        setInputValue(displayDateStr(newDate));
        setIsOpen(false);
    };

    const formatDateStr = (dateObj) => {
        if (!dateObj) return '';
        const yyyy = dateObj.getFullYear();
        const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
        const dd = String(dateObj.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const displayDateStr = (dateObj) => {
        if (!dateObj) return '';
        const yyyy = dateObj.getFullYear();
        const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
        const dd = String(dateObj.getDate()).padStart(2, '0');
        return `${dd}-${mm}-${yyyy}`;
    };

    return (
        <div className="relative w-full" ref={containerRef}>
            {/* Hidden input for form submission */}
            <input type="hidden" name={name} value={formatDateStr(selectedDate)} required={required} />
            
            {/* Trigger Input */}
            <div className={`flex items-center justify-between overflow-hidden relative ${className}`}>
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => {
                        const val = e.target.value;
                        setInputValue(val);
                        // Support both dd-mm-yyyy and yyyy-mm-dd input
                        const matchDmy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(val);
                        const matchYmd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
                        if (matchDmy) {
                            const d = new Date(`${matchDmy[3]}-${matchDmy[2]}-${matchDmy[1]}`);
                            if (!isNaN(d.getTime())) {
                                setSelectedDate(d);
                                setCurrentMonth(d);
                            }
                        } else if (matchYmd) {
                            const d = new Date(val);
                            if (!isNaN(d.getTime())) {
                                setSelectedDate(d);
                                setCurrentMonth(d);
                            }
                        } else if (val === '') {
                            setSelectedDate(null);
                        }
                    }}
                    onFocus={() => setIsOpen(true)}
                    placeholder="DD-MM-YYYY"
                    className="w-full h-full bg-transparent outline-none border-none text-primary placeholder-muted"
                />
                <CalendarIcon 
                    className="w-4 h-4 text-muted cursor-pointer flex-shrink-0 hover:text-primary transition-colors" 
                    onClick={() => setIsOpen(!isOpen)}
                />
            </div>

            {/* Popover */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.15 }}
                        className="absolute z-50 top-full mt-2 left-0 w-64 bg-surface rounded-xl shadow-xl border border-border overflow-hidden"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-3 border-b border-border bg-surface-2/50">
                            <button type="button" onClick={handlePrevMonth} className="p-1 hover:bg-surface rounded-md text-muted hover:text-primary transition-colors">
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <div className="font-bold text-sm">
                                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                            </div>
                            <button type="button" onClick={handleNextMonth} className="p-1 hover:bg-surface rounded-md text-muted hover:text-primary transition-colors">
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Calendar Grid */}
                        <div className="p-3">
                            <div className="grid grid-cols-7 gap-1 mb-2">
                                {dayNames.map(day => (
                                    <div key={day} className="text-center text-[10px] font-bold text-muted uppercase">
                                        {day}
                                    </div>
                                ))}
                            </div>
                            <div className="grid grid-cols-7 gap-1">
                                {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                                    <div key={`empty-${i}`} className="w-7 h-7" />
                                ))}
                                {Array.from({ length: daysInMonth }).map((_, i) => {
                                    const day = i + 1;
                                    const isSelected = selectedDate && 
                                        selectedDate.getDate() === day && 
                                        selectedDate.getMonth() === currentMonth.getMonth() && 
                                        selectedDate.getFullYear() === currentMonth.getFullYear();
                                    
                                    const isToday = new Date().getDate() === day && 
                                        new Date().getMonth() === currentMonth.getMonth() && 
                                        new Date().getFullYear() === currentMonth.getFullYear();

                                    return (
                                        <button
                                            key={day}
                                            type="button"
                                            onClick={() => handleSelectDate(day)}
                                            className={`
                                                w-7 h-7 flex items-center justify-center rounded-md text-xs font-medium transition-colors
                                                ${isSelected ? 'bg-brand text-inverse shadow-brand-sm' : 
                                                  isToday ? 'bg-brand/10 text-brand border border-brand/20' : 
                                                  'text-primary hover:bg-surface-2'}
                                            `}
                                        >
                                            {day}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
